// src/pages/ChatRoomMitra.js
import React, { useEffect, useState } from 'react'
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  getDocs,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage' 
import { db } from '../firebaseConfig'
import { ArrowLeft, Plus, X, Check, CheckCheck, Image as ImageIcon } from 'lucide-react'

const storage = getStorage()

export default function ChatRoomMitra({ mitraName, chatId, onBack }) {
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [sessionActive, setSessionActive] = useState(true)

  const [isDriverTyping, setIsDriverTyping] = useState(false)
  const [isDriverOnline, setIsDriverOnline] = useState(false)
  const [typingTimeout, setTypingTimeout] = useState(null)

  const [quickReplies, setQuickReplies] = useState([
    'Baik kak, saya bantu cek ya.',
    'Mohon tunggu sebentar ya kak.',
    'Apakah ada hal lain yang bisa saya bantu?',
    'Baik kak, segera saya proses.',
    'Boleh kirimkan foto bukti / screenshot?',
  ])
  
  const [isAddingReply, setIsAddingReply] = useState(false)
  const [tempReply, setTempReply] = useState('')

  const handleAddReply = () => {
    if (!tempReply.trim()) return
    setQuickReplies([...quickReplies, tempReply.trim()]) 
    setTempReply('')
    setIsAddingReply(false) 
  }

  // ======================================================
  // FUNGSI UPLOAD GAMBAR & KIRIM
  // ======================================================
  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!sessionActive) return alert('Sesi tertutup. Buka sesi dulu.')
    if (!chatId || !currentSessionId) return

    try {
      setLoading(true)
      const storageRef = ref(storage, `chat_images/${chatId}/${Date.now()}_${file.name}`)
      const snapshot = await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)

      const msgRef = collection(db, 'chats', chatId, 'sessions', currentSessionId, 'messages')

      await addDoc(msgRef, {
        text: 'Mengirim gambar...', 
        imageUrl: downloadURL,      
        senderId: 'admin',
        senderName: 'Admin Zimo',
        isAdmin: true,
        type: 'image',              
        createdAt: serverTimestamp(),
        timestamp: serverTimestamp(),
      })

      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage: '📷 [Gambar]',
        lastUpdated: serverTimestamp(),
      })

    } catch (error) {
      console.error("Upload gagal:", error)
      alert("Gagal mengupload gambar.")
    } finally {
      setLoading(false)
    }
  }

  const renderMessageContent = (msg) => {
    if (msg.type === 'image' && msg.imageUrl) {
      return (
        <div className="mt-1">
          <img 
            src={msg.imageUrl} 
            alt="Sent attachment" 
            className="rounded-lg max-w-[200px] max-h-[200px] object-cover border border-gray-600 cursor-pointer"
            onClick={() => window.open(msg.imageUrl, '_blank')}
          />
        </div>
      )
    }

    const text = msg.text || ''
    const urlRegex = /(https?:\/\/[^\s]+)/g
    
    if (text.match(urlRegex)) {
      const parts = text.split(urlRegex)
      return (
        <span>
          {parts.map((part, i) => 
            part.match(urlRegex) ? (
              <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline hover:text-blue-300 break-all">
                {part}
              </a>
            ) : part
          )}
        </span>
      )
    }

    return text
  }

  // ======================================================
  // 1️⃣ Load Chat Meta & Session 
  // ======================================================
  useEffect(() => {
    if (!chatId) return

    const chatRef = doc(db, 'chats', chatId)
    updateDoc(chatRef, { adminPresent: true }).catch(() => {})

    const unsubSession = onSnapshot(chatRef, (snap2) => {
      if (!snap2.exists()) return
      const data = snap2.data()
      setSessionActive(data.sessionActive === true)
      setCurrentSessionId(data.currentSessionId || null)
      setIsDriverOnline(data.driverPresent === true)
      setIsDriverTyping(data.driverTyping === true)
      
      if (data.sessionActive !== true) setMessages([])
    })
    
    return () => { 
      unsubSession && unsubSession() 
      updateDoc(chatRef, { adminPresent: false, adminTyping: false }).catch(() => {})
    }
  }, [chatId])

  // ======================================================
  // 2️⃣ Load Messages Listener
  // ======================================================
  useEffect(() => {
    if (!chatId || !currentSessionId) {
      setLoading(false)
      return
    }

    setLoading(true)
    const msgRef = collection(db, 'chats', chatId, 'sessions', currentSessionId, 'messages')
    const msgQuery = query(msgRef, orderBy('timestamp', 'asc'))

    const unsubMsg = onSnapshot(msgQuery, (snapshot) => {
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().createdAt || d.data().timestamp
      }))
      setMessages(data)
      setLoading(false)

      snapshot.docs.forEach((docSnap) => {
        if (docSnap.data().senderId !== 'admin' && docSnap.data().status !== 'read') {
          updateDoc(docSnap.ref, { status: 'read' }).catch(() => {})
        }
      })
    })

    return () => unsubMsg()
  }, [chatId, currentSessionId])
  
  // ======================================================
  // 3️⃣ ADMIN KIRIM PESAN 
  // ======================================================
  const handleSend = async () => {
    if (!newMessage.trim() || !chatId || !currentSessionId) return

    const msgRef = collection(db, 'chats', chatId, 'sessions', currentSessionId, 'messages')

    const payload = {
      text: newMessage.trim(),
      senderId: 'admin', 
      senderName: 'Admin Zimo',
      isAdmin: true, 
      createdAt: serverTimestamp(),
      timestamp: serverTimestamp(),
      type: 'text'
    }

    try {
      await addDoc(msgRef, payload)
      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage: newMessage.trim(),
        lastUpdated: serverTimestamp(),
        lastTimestamp: serverTimestamp(), 
      })
      setNewMessage('')
    } catch (e) {
       console.error("Gagal mengirim pesan:", e)
    }
  }

  // ======================================================
  // 3️⃣b QUICK REPLY SEND 
  // ======================================================
  const handleQuickReply = async (text) => {
    if (!sessionActive) {
      alert('Sesi telah ditutup oleh admin.')
      return
    }
    if (!text || !chatId || !currentSessionId) return

    const msgRef = collection(db, 'chats', chatId, 'sessions', currentSessionId, 'messages')

    const payload = {
      text: text,
      senderId: 'admin',
      senderName: 'Admin Zimo',
      isAdmin: true,               
      createdAt: serverTimestamp(), 
      timestamp: serverTimestamp(), 
      type: 'text'
    }

    try {
      await addDoc(msgRef, payload)
      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage: text,
        lastUpdated: serverTimestamp()
      })
    } catch (e) {
      console.error("Gagal kirim quick reply:", e)
      alert("Gagal mengirim pesan cepat.")
    }
  }

  // ======================================================
  // 🔥 AUTO DELETE CHAT 5 MENIT SETELAH CLOSE SESSION
  // ======================================================
  const scheduleAutoDelete = async (chatId, sessionId) => {
    setTimeout(async () => {
      try {
        const root = await getDoc(doc(db, 'chats', chatId))
        if (root.data()?.sessionActive === true) return

        const msgs = await getDocs(
          collection(db, 'chats', chatId, 'sessions', sessionId, 'messages')
        )
        msgs.forEach((m) => deleteDoc(m.ref))

        await deleteDoc(doc(db, 'chats', chatId, 'sessions', sessionId))
      } catch (e) {
        console.log('Auto delete gagal:', e)
      }
    }, 5 * 60 * 1000)
  }

  // ======================================================
  // 4️⃣ ADMIN OPEN SESSION 
  // ======================================================
  const openSession = async () => {
    if (!chatId) return
    
    const chatRef = doc(db, 'chats', chatId)
    const snap = await getDoc(chatRef)
    const data = snap.data()
    let sessionId = data?.currentSessionId

    if (!sessionId) {
      sessionId = `S_${Date.now()}`
      await setDoc(doc(db, 'chats', chatId, 'sessions', sessionId), { startedAt: serverTimestamp(), endedAt: null }, { merge: true })
      await updateDoc(chatRef, { currentSessionId: sessionId })
    }
    setCurrentSessionId(sessionId)
    await setDoc(chatRef, { sessionActive: true, openedByAdmin: true, waiting: false, lastTimestamp: serverTimestamp() }, { merge: true })
    
    await addDoc(collection(db, 'chats', chatId, 'sessions', sessionId, 'messages'), {
      senderId: 'system',
      text: 'Admin telah memulai sesi chat.',
      timestamp: serverTimestamp(),
    })
  }

  // ======================================================
  // 5️⃣ ADMIN CLOSE SESSION
  // ======================================================
  const closeSession = async () => {
    if (!chatId || !currentSessionId) return
    const chatRef = doc(db, 'chats', chatId)
    
    await setDoc(doc(db, 'chats', chatId, 'sessions', currentSessionId), { endedAt: serverTimestamp() }, { merge: true })
    await setDoc(chatRef, { sessionActive: false, openedByAdmin: false, waiting: false, currentSessionId: null }, { merge: true })
    
    await addDoc(collection(db, 'chats', chatId, 'sessions', currentSessionId, 'messages'), {
      senderId: 'system',
      text: 'Sesi chat ditutup oleh admin.',
      timestamp: serverTimestamp(),
    })
    
    setCurrentSessionId(null)
    setMessages([])
    scheduleAutoDelete(chatId, currentSessionId)
  }

  if (loading)
    return (
      <div className="p-6 text-gray-500 text-sm text-center">
        Memuat percakapan...
      </div>
    )

  return (
    <div className="flex flex-col h-[calc(100vh-220px)] bg-gray-950 border border-gray-800 rounded-xl">
      {/* HEADER */}
      <div className="flex items-center justify-between bg-gray-900 px-4 py-3 border-b border-gray-800 rounded-t-xl">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-800 text-gray-300 transition"
          >
            <ArrowLeft size={20} />
          </button>

          <div>
            <div className="flex items-center gap-2">
              <p className="text-gray-200 font-semibold text-lg">{mitraName}</p>
              {isDriverOnline && sessionActive && <span className="bg-green-500 w-2.5 h-2.5 rounded-full shadow-[0_0_5px_#22c55e]"></span>}
            </div>
            
            {/* 🔥 STATUS SESI & TYPING */}
            <p className="text-[12px] text-gray-400 font-medium flex items-center gap-1 mt-0.5">
              {sessionActive ? (isDriverTyping ? <span className="text-blue-400 italic">sedang mengetik...</span> : (isDriverOnline ? <span className="text-green-400">Online</span> : 'Sesi Aktif')) : 'Sesi Ditutup'}
            </p>
          </div>
        </div>

        {/* TOMBOL OPEN / CLOSE */}
        <div className="flex gap-2">
          <button
            onClick={openSession}
            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs"
          >
            OPEN
          </button>

          <button
            onClick={closeSession}
            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
          >
            CLOSE
          </button>
        </div>
      </div>

      {/* PESAN */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-gray-500 text-sm text-center mt-10">
            Belum ada pesan.
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${
                msg.senderId === 'admin' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`px-4 py-2 rounded-2xl max-w-[70%] text-[15px] leading-relaxed flex flex-col ${
                  msg.senderId === 'admin'
                    ? 'bg-gray-800 text-gray-200 rounded-br-none shadow-md'
                    : 'bg-gray-900 text-gray-200 rounded-bl-none border border-gray-800 shadow-md'
                }`}
              >
                {renderMessageContent(msg)}

                {/* --- TIMESTAMP & CENTANG STATUS --- */}
                <div className={`flex items-center gap-1 mt-1 text-[10px] ${msg.senderId === 'admin' ? 'justify-end text-gray-400' : 'justify-start text-gray-500'}`}>
                  <span>{msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleTimeString('id-ID', {hour: '2-digit', minute:'2-digit'}) : ''}</span>
                  {msg.senderId === 'admin' && (
                    <span>
                      {msg.status === 'read' ? <CheckCheck size={14} className="text-blue-400" /> 
                      : msg.status === 'delivered' ? <CheckCheck size={14} className="text-gray-400" />
                      : <Check size={14} className="text-gray-500" />}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ⭐ QUICK REPLY BAR */}
      {sessionActive && (
        <div className="flex items-center gap-2 p-2 overflow-x-auto bg-gray-900 border-t border-gray-800">
          {isAddingReply ? (
            <div className="flex items-center gap-1 bg-gray-800 rounded px-2 py-1 border border-gray-700">
              <input 
                autoFocus
                value={tempReply}
                onChange={(e) => setTempReply(e.target.value)}
                placeholder="Ketik balasan..."
                className="bg-transparent text-white text-xs outline-none w-32"
                onKeyDown={(e) => e.key === 'Enter' && handleAddReply()}
              />
              <button onClick={handleAddReply} className="text-green-500 hover:text-green-400">
                <Check size={14} />
              </button>
              <button onClick={() => setIsAddingReply(false)} className="text-red-500 hover:text-red-400">
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsAddingReply(true)}
              className="flex items-center justify-center px-3 py-1 bg-blue-900 hover:bg-blue-800 text-blue-200 rounded text-xs border border-blue-800"
              title="Tambah Balasan Cepat"
            >
              <Plus size={14} className="mr-1"/> Baru
            </button>
          )}

          {quickReplies.map((txt, idx) => (
            <button
              key={idx}
              onClick={() => handleQuickReply(txt)}
              className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded text-xs whitespace-nowrap border border-gray-700 transition"
            >
              {txt}
            </button>
          ))}
        </div>
      )}

      {/* INPUT */}
      <div className="flex items-center gap-2 p-3 bg-gray-900 border-t border-gray-800 rounded-b-xl">
        <label className={`p-2 rounded-full cursor-pointer transition ${sessionActive ? 'hover:bg-gray-800 text-gray-400' : 'opacity-50 cursor-not-allowed'}`}>
          <input 
            type="file" 
            accept="image/*" 
            className="hidden" 
            onChange={handleImageUpload}
            disabled={!sessionActive}
          />
          <ImageIcon size={20} />
        </label>

        <input
          value={newMessage}
          onChange={(e) => {
            setNewMessage(e.target.value)
            if (chatId) {
              updateDoc(doc(db, 'chats', chatId), { adminTyping: true }).catch(() => {})
              if (typingTimeout) clearTimeout(typingTimeout)
              setTypingTimeout(setTimeout(() => updateDoc(doc(db, 'chats', chatId), { adminTyping: false }), 2000))
            }
          }}
          type="text"
          placeholder="Tulis pesan atau tempel Link..."
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          className="flex-1 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 outline-none focus:ring-1 focus:ring-gray-700"
          disabled={!sessionActive}
        />
        <button
          onClick={handleSend}
          className="bg-gray-800 hover:bg-gray-700 text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition"
          disabled={!sessionActive}
        >
          Kirim
        </button>
      </div>
    </div>
  )
}